/**
 * WorkerValidator - Utility for validating worker state
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for checking worker initialization state
 * and throwing appropriate errors when the worker is not ready.
 */

/**
 * Error message for uninitialized worker
 */
export const WORKER_NOT_INITIALIZED_ERROR = 'Worker not initialized. Call initialize() first.';

/**
 * Check if worker is ready
 * 
 * @param worker - Worker instance (can be null)
 * @param isReady - Ready state flag
 * @returns True if worker is ready, false otherwise
 */
export function isWorkerReady(worker: Worker | null, isReady: boolean): boolean {
  return worker !== null && isReady;
}

/**
 * Validate that worker is ready, throwing an error if not
 * 
 * @param worker - Worker instance (can be null)
 * @param isReady - Ready state flag
 * @throws Error if worker is not ready
 */
export function validateWorkerReady(worker: Worker | null, isReady: boolean): void {
  if (!isWorkerReady(worker, isReady)) {
    throw new Error(WORKER_NOT_INITIALIZED_ERROR);
  }
}

/**
 * Get worker readiness status for logging
 * 
 * @param worker - Worker instance (can be null)
 * @param isReady - Ready state flag
 * @returns Object with readiness status details
 */
export function getWorkerReadinessStatus(
  worker: Worker | null,
  isReady: boolean
): { hasWorker: boolean; isReady: boolean; isFullyReady: boolean } {
  return {
    hasWorker: worker !== null,
    isReady,
    isFullyReady: isWorkerReady(worker, isReady)
  };
}

